import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, Users, Phone, Target, TrendingUp, TrendingDown,
  CheckCircle, XCircle, PhoneCall, MessageSquare, Flame, Clock,
  ArrowLeft, RefreshCw, User
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const theme = {
  bg: '#F7F4EF',
  orange: '#E8621A',
  ink: '#1A1713',
  amber: '#B8760A',
  blue: '#1A4A8A',
  green: '#1D7A4F',
  red: '#C0392B',
  purple: '#7c3aed',
};

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMember, setCurrentMember] = useState(null);

  useEffect(() => {
    // Get current member
    const member = localStorage.getItem('crm_member');
    if (member) {
      setCurrentMember(JSON.parse(member));
    }
    
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/statistics`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link to="/crm" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="font-bebas text-xl flex items-center gap-2" style={{ color: theme.ink }}>
                <BarChart3 className="w-5 h-5" style={{ color: theme.orange }} />
                STATISTIQUES
              </h1>
            </div>
            <button
              onClick={fetchStats}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <RefreshCw className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Main KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPICard
            label="Total Leads"
            value={stats?.total_leads || 0}
            icon={Users}
            color={theme.ink}
          />
          <KPICard
            label="Taux de conversion"
            value={`${stats?.conversion_rate || 0}%`}
            icon={TrendingUp}
            color={theme.green}
            subtitle={`${stats?.won || 0} gagnés / ${(stats?.won || 0) + (stats?.lost || 0)} closés`}
          />
          <KPICard
            label="Leads chauds"
            value={stats?.hot_leads || 0}
            icon={Flame}
            color={theme.red}
            highlight
          />
          <KPICard
            label="À relancer"
            value={stats?.needs_followup || 0}
            icon={Clock}
            color={theme.amber}
          />
        </div>

        {/* Activity Stats */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 mb-8">
          <h2 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5" style={{ color: theme.orange }} />
            Activité Équipe
          </h2>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: 'rgba(232,98,26,0.1)' }}>
                <PhoneCall className="w-8 h-8" style={{ color: theme.orange }} />
              </div>
              <p className="text-3xl font-black text-gray-800">{stats?.activity?.total_calls || 0}</p>
              <p className="text-sm text-gray-500">Appels</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: 'rgba(29,122,79,0.1)' }}>
                <CheckCircle className="w-8 h-8" style={{ color: theme.green }} />
              </div>
              <p className="text-3xl font-black text-gray-800">{stats?.activity?.total_pickups || 0}</p>
              <p className="text-sm text-gray-500">Décrochés</p>
              <p className="text-xs text-green-600 font-bold mt-1">{stats?.pickup_rate || 0}% taux</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: 'rgba(26,74,138,0.1)' }}>
                <MessageSquare className="w-8 h-8" style={{ color: theme.blue }} />
              </div>
              <p className="text-3xl font-black text-gray-800">{stats?.activity?.total_messages || 0}</p>
              <p className="text-sm text-gray-500">Messages</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Pipeline */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <h2 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" style={{ color: theme.blue }} />
              Pipeline
            </h2>
            <div className="space-y-3">
              <PipelineBar label="Nouveau" value={stats?.status_counts?.nouveau || 0} total={stats?.total_leads} color={theme.orange} />
              <PipelineBar label="Contact" value={stats?.status_counts?.contact_en_cours || 0} total={stats?.total_leads} color={theme.amber} />
              <PipelineBar label="RDV pris" value={stats?.status_counts?.rdv_pris || 0} total={stats?.total_leads} color={theme.blue} />
              <PipelineBar label="No Show" value={stats?.status_counts?.no_show || 0} total={stats?.total_leads} color="#9CA3AF" />
              <PipelineBar label="RDV fait" value={stats?.status_counts?.rdv_fait || 0} total={stats?.total_leads} color={theme.purple} />
              <PipelineBar label="🔥 Chaud" value={stats?.status_counts?.chaud || 0} total={stats?.total_leads} color={theme.red} />
              <PipelineBar label="Relance" value={stats?.status_counts?.relance || 0} total={stats?.total_leads} color={theme.amber} />
              <PipelineBar label="💰 Closé" value={stats?.status_counts?.gagne || 0} total={stats?.total_leads} color={theme.green} />
              <PipelineBar label="❌ Perdu" value={stats?.status_counts?.perdu || 0} total={stats?.total_leads} color={theme.red} />
            </div>
          </div>

          {/* Team Performance */}
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <h2 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" style={{ color: theme.amber }} />
              Performance Équipe
            </h2>
            
            {/* Setters */}
            <div className="mb-6">
              <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2">📞 Setters</p>
              {Object.keys(stats?.setter_counts || {}).length === 0 ? (
                <p className="text-sm text-gray-400">Aucun setter assigné</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(stats?.setter_counts || {}).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between p-2 rounded-lg bg-amber-50">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">
                          {name.charAt(0)}
                        </div>
                        <span className="font-medium text-gray-800">{name}</span>
                      </div>
                      <span className="font-bold text-amber-700">{count} leads</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Closers */}
            <div>
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">🎯 Closers</p>
              {Object.keys(stats?.closer_counts || {}).length === 0 ? (
                <p className="text-sm text-gray-400">Aucun closer assigné</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(stats?.closer_counts || {}).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between p-2 rounded-lg bg-blue-50">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                          {name.charAt(0)}
                        </div>
                        <span className="font-medium text-gray-800">{name}</span>
                      </div>
                      <span className="font-bold text-blue-700">{count} leads</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200">
          <h2 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" style={{ color: theme.green }} />
            Entonnoir de Conversion
          </h2>
          <div className="flex items-end justify-between gap-4 h-48">
            <FunnelBar label="Total" value={stats?.total_leads || 0} maxValue={stats?.total_leads || 1} color={theme.ink} />
            <FunnelBar label="Contactés" value={(stats?.status_counts?.contact_en_cours || 0) + (stats?.status_counts?.rdv_pris || 0) + (stats?.status_counts?.rdv_fait || 0) + (stats?.status_counts?.chaud || 0) + (stats?.status_counts?.gagne || 0)} maxValue={stats?.total_leads || 1} color={theme.amber} />
            <FunnelBar label="RDV" value={(stats?.status_counts?.rdv_pris || 0) + (stats?.status_counts?.rdv_fait || 0) + (stats?.status_counts?.chaud || 0) + (stats?.status_counts?.gagne || 0)} maxValue={stats?.total_leads || 1} color={theme.blue} />
            <FunnelBar label="Chauds" value={(stats?.status_counts?.chaud || 0) + (stats?.status_counts?.gagne || 0)} maxValue={stats?.total_leads || 1} color={theme.orange} />
            <FunnelBar label="Closés" value={stats?.status_counts?.gagne || 0} maxValue={stats?.total_leads || 1} color={theme.green} />
          </div>
        </div>
      </main>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ label, value, icon: Icon, color, subtitle, highlight }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-white rounded-xl p-4 border ${highlight ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
  >
    <div className="flex items-start justify-between mb-2">
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="text-3xl font-black" style={{ color }}>{value}</p>
    <p className="text-sm text-gray-500">{label}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </motion.div>
);

// Pipeline Bar Component
const PipelineBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-bold" style={{ color }}>{value}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5 }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
};

// Funnel Bar Component
const FunnelBar = ({ label, value, maxValue, color }) => {
  const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="flex-1 flex flex-col items-center">
      <div className="w-full h-40 flex items-end">
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: `${height}%` }}
          transition={{ duration: 0.5 }}
          className="w-full rounded-t-lg"
          style={{ backgroundColor: color }}
        />
      </div>
      <p className="text-lg font-bold mt-2" style={{ color }}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

export default StatsPage;
